
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { TrendingDown, ArrowLeft, ArrowRight } from "lucide-react";
import type { BallTendency } from "@/hooks/level3/types";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface IronScoreButtonsProps {
  onScoreChange: (score: number, tendency?: BallTendency) => void;
}

const IronScoreButtons: React.FC<IronScoreButtonsProps> = ({ onScoreChange }) => {
  const { t } = useTranslation('training');
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [showTendency, setShowTendency] = useState(false);
  
  const buttonVariants = {
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  const handleScoreClick = (score: number) => {
    setSelectedScore(score);
    
    if (score > 0) {
      setShowTendency(true);
    } else {
      onScoreChange(score, null);
      setSelectedScore(null);
    }
  };

  const handleTendencyClick = (tendency: BallTendency) => {
    if (selectedScore !== null) {
      onScoreChange(selectedScore, tendency);
      setSelectedScore(null);
      setShowTendency(false);
    }
  };

  const resetSelection = () => {
    setSelectedScore(null);
    setShowTendency(false);
  };

  if (showTendency) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 p-3 rounded-md border border-blue-100 text-center mb-2">
          <p className="text-blue-800 font-medium">{t('interface.scoring.iron.selectTendency')}</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Button 
            onClick={() => handleTendencyClick('left')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <ArrowLeft className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('interface.scoring.iron.left')}</span>
          </Button>
          <Button 
            onClick={() => handleTendencyClick('center')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <TrendingDown className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('interface.scoring.iron.center')}</span>
          </Button>
          <Button 
            onClick={() => handleTendencyClick('right')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <ArrowRight className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('interface.scoring.iron.right')}</span>
          </Button>
        </div>
        <Button 
          onClick={resetSelection}
          className="h-10 w-full text-sm bg-gray-200 hover:bg-gray-300 text-gray-700"
        >
          {t('interface.scoring.iron.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      <Button
        onClick={() => handleScoreClick(10)}
        className="h-12 font-medium bg-emerald-500 hover:bg-emerald-600 text-white"
      >
        {t('interface.scoring.iron.ballIn')} (10 pts)
      </Button>
      <Button
        onClick={() => handleScoreClick(6)}
        className="h-12 font-medium bg-green-500 hover:bg-green-600 text-white"
      >
        {t('interface.scoring.iron.at2m')} (6 pts)
      </Button>
      <Button
        onClick={() => handleScoreClick(4)}
        className="h-12 font-medium bg-blue-500 hover:bg-blue-600 text-white"
      >
        {t('interface.scoring.iron.at4m')} (4 pts)
      </Button>
      <Button
        onClick={() => handleScoreClick(2)}
        className="h-12 font-medium bg-amber-400 hover:bg-amber-500 text-white"
      >
        {t('interface.scoring.iron.at6m')} (2 pts)
      </Button>
      <Button
        onClick={() => handleScoreClick(1)}
        className="h-12 font-medium bg-orange-500 hover:bg-orange-600 text-white"
      >
        {t('interface.scoring.iron.at10m')} (1 pt)
      </Button>
      <Button
        onClick={() => handleScoreClick(0)}
        className="h-12 font-medium bg-red-500 hover:bg-red-600 text-white"
      >
        {t('interface.scoring.iron.outsideCircles')} (0 pt)
      </Button>
    </div>
  );
};

export default IronScoreButtons;
